import { colors } from 'fitify-ui'
import type { LinkTypes } from 'fitify-ui-landing/src/@types'
import NativeImage from 'fitify-ui-landing/src/components/Image/NativeImage'
import { createRedirectLinkWithLocale } from 'fitify-ui-landing/src/utils/link'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import { NextSeo, type NextSeoProps, OrganizationJsonLd } from 'next-seo'

import Newsletter from '@/components/blocks/Newsletter/Newsletter'
import Navigation from '@/components/Navigation/Navigation'
import { commonData } from '@/data/common'
import { footerCopy, footerNavigation, footerSocial } from '@/data/footer'
import { navigationData } from '@/data/navbar'
import { useLanguageTags } from '@/hooks/useLanguageTags'

const Footer = dynamic(() => import('@/components/blocks/Footer/Footer'))

export const Layout = ({
  seo,
  children,
  newsletterBackgroundColor = colors.Mantis100,
}: {
  seo?: Pick<NextSeoProps, 'description' | 'openGraph' | 'title'>
  children: React.ReactNode
  newsletterBackgroundColor?: string
}) => {
  const { i18n } = useTranslation()
  const { canonical, languageAlternates } = useLanguageTags()

  const enhanceWithLocale = (locale: string, item: LinkTypes.Navigation) => {
    if (item.isOnboarding) {
      return {
        ...item,
        link: createRedirectLinkWithLocale(item.link, locale),
      }
    } else {
      return {
        ...item,
      }
    }
  }

  const enhancedNavigationData = navigationData.map((item) =>
    enhanceWithLocale(i18n.language, item)
  )

  const enhancedFooterNavigation = footerNavigation.map((item) =>
    enhanceWithLocale(i18n.language, item)
  )

  return (
    <>
      <NextSeo
        title={seo?.title}
        description={seo?.description}
        openGraph={seo?.openGraph}
        nofollow={false}
        noindex={false}
        canonical={canonical}
        languageAlternates={languageAlternates}
      />

      <OrganizationJsonLd
        type="Organization"
        logo="https://gofitify.com/assets/images/logo.svg"
        legalName={commonData.company.legalName}
        name={commonData.company.name}
        description={
          'We develop fitness and health apps that lead you to a better lifestyle.'
        }
        foundingDate={commonData.company.foundingDate}
        address={commonData.company.address}
        contactPoint={[
          {
            contactType: 'customer service',
            email: commonData.contact.supportEmail,
          },
        ]}
        url={commonData.contact.landingUrl}
      />

      <Navigation
        items={enhancedNavigationData}
        options={{ mobileEnabled: false }}
        logo={
          <NativeImage
            src={'/assets/images/logo/logo.webp'}
            srcSet={
              '/assets/images/logo/logo.webp, /assets/images/logo/logo@2x.webp 2x'
            }
            alt={'Nutria Logo'}
            width={'108'}
            height={'48'}
          />
        }
      />

      <div>
        {children}
        <div style={{ background: `${newsletterBackgroundColor}` }}>
          <Newsletter
            blockText="cl_landing_emailing_block_text"
            emailPlaceholderText="cl_landing_emailing_block_your_email"
            submitButtonText="cl_landing_emailing_block_button"
            successMessage="cl_landing_newsletter_success_messages"
            errorMessage="cl_landing_newsletter_error"
          />
        </div>

        <Footer
          copyLinks={footerCopy}
          socialLinks={footerSocial}
          navigationLinks={enhancedFooterNavigation}
          logo={
            <NativeImage
              src={'/assets/images/logo/logo.webp'}
              srcSet={
                '/assets/images/logo/logo.webp, /assets/images/logo/logo@2x.webp 2x'
              }
              alt={'Nutria Logo'}
              width={'140'}
              height={'62'}
            />
          }
        />
      </div>
    </>
  )
}
